<i18n>
[
    "global__viewAll",
    "product__recommendationTray--label",
]
</i18n>

<template>
    <div v-if="shouldShowProductTray" :style="style">
        <ProductTray
            :id="id"
            :listId="listId"
            :listName="listName"
            :contentModuleId="contentModuleId"
            :contentTitle="contentTitle"
            :products="products"
            :headerLink="trayHeaderLink"
            :headerTitle="headerTitle"
            :headerSubtitle="headerSubtitle"
            :totalProducts="totalProducts"
            :mobilePeek="mobilePeek"
            :mobilePeekPercent="mobilePeekPercent"
            :mediumPeekPercent="mediumPeekPercent"
            :isFullBleedDesktop="true"
            :displayCounter="displayCounter"
            :desktopSlideSize="4"
            :xlDesktopSlideSize="5"
            :mediumSlideSize="mediumSlideSize"
            :mobileSlideSize="1"
            :translucentButtons="translucentButtons"
            :lightButtons="lightButtons"
            :lightInfoContainer="false"
            :catalogResponseId="catalogResponseId"
            :showProductInfo="showProductInfo"
            :filters="recommendationQuery"
            :analyticsType="analyticsType"
            :traceId="traceId"
            :fitGuidanceId="fitGuidanceId"
            :hasEnteredViewport="hasEnteredViewport"
            :fillEmptyPageWithBlankTiles="false"
            :isLoaded="isLoaded"
        />
    </div>
</template>

<script setup>
import { isEmpty, merge } from 'lodash-es';
import { computed, ref, watch, provide, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useR15Svc } from '~coreModules/core/js/composables/useR15Svc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';
import { useActions, useGetters, useState } from '~coreModules/core/js/composables/vuexHelpers';

import { CATALOG_QUERY_IN_STOCK, CATALOG_QUERY_SIZE } from '~coreModules/catalog/js/catalog-constants';
import { LARGE_WITH_TILE_PEEK_MOBILE } from '~coreModules/core/js/carousel-constants';
import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { SITE_SETTINGS_MODULE_NAME } from '~coreModules/site-settings/js/site-settings-store';

import { shapeShopTheLookProducts } from '~routes/product/js/product-utils';
import { getProductsByChoiceId } from '~routes/product/js/product-api';
// NU_EXPERIMENT: productSimilarityPdp
// https://urbnit.atlassian.net/browse/TYP-34099
// remove below if necessary when experiment is over
import { getSimilarProductsByChoiceId, getVisuallySimilarProductsByChoiceId } from '~routes/browse/js/browse-api';

import { CATALOG_SHOP_THE_LOOK_TRAY_LIST_NAME } from '~modules/catalog/js/catalog-constants';
import { getCatalogTrayAnalyticsData } from '~modules/catalog/js/catalog-utils';
import { PROFILE_MODULE_NAME } from '~modules/profile/js/profile-store';
import { rentalRouteNames } from '~modules/core/js/router-constants';
import { injectedPrePurchase } from '~modules/core/js/provider-symbol-constants';

import productTrayProps from '~coreModules/core/js/ui/productTrayProps';
import ProductTray from '~modules/core/components/ui/ProductTray.vue';

const props = defineProps({
    ...productTrayProps,
    choiceId: {
        type: String,
        required: true,
    },
    categoryQuery: {
        type: Object,
        default: null,
    },
    maxProducts: {
        type: Number,
        default: 15,
    },
    shouldShowHeaderLink: {
        type: Boolean,
        default: true,
    },
    lightInfoContainer: {
        type: Boolean,
        default: true,
    },
    applySizePreferencesAndAvailability: {
        type: Boolean,
        default: false,
    },
    paddingBottom: {
        type: String,
        default: null,
    },
    analyticsType: {
        type: String,
        default: null,
    },
    productsRelatedToColorData: {
        type: Array,
        default: () => [],
    },
    hasProductsRelatedToColorData: {
        type: Boolean,
        default: false,
    },
    recTrayType: {
        type: String,
        default: null,
    },
    traceId: {
        type: String,
        default: null,
    },
    fitGuidanceId: {
        type: String,
        default: null,
    },
    isBuyMode: {
        type: Boolean,
        default: false,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
    mobilePeekPercent: {
        type: Number,
        default: LARGE_WITH_TILE_PEEK_MOBILE,
    },
});

const route = useRoute();
const { t } = useI18n();
const logger = useLogger();
const r15Svc = useR15Svc();

provide(injectedPrePurchase, computed(() => props.isBuyMode));

const {
    applyAvailabilityFilterToTrays,
    applySizeFiltersToTrays,
    applyAvailabilityFilterPreference,
} = useState(SITE_SETTINGS_MODULE_NAME, {
    applyAvailabilityFilterToTrays: state => state.contentfulAppConfig.applyAvailabilityFilterToTrays,
    applySizeFiltersToTrays: state => state.contentfulAppConfig.applySizeFiltersToTrays,
    applyAvailabilityFilterPreference: state => state.preferences?.applyAvailabilityFilterPreference,
});

const { preferredSizeList } = useGetters(PROFILE_MODULE_NAME, ['preferredSizeList']);
const { catalogFilterWhitelist } = useGetters(SITE_SETTINGS_MODULE_NAME, ['catalogFilterWhitelist']);

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

const products = ref([]);
const catalogResponseId = ref(null);
const isLoaded = ref(false);

const totalProducts = computed(() => {
    if (products.value.length < props.maxProducts) {
        return products.value.length;
    }

    return props.maxProducts;
});

const defaultHeaderLink = computed(() => ({
    linkPath: {
        name: rentalRouteNames.browseSimilar,
        params: {
            choiceId: props.choiceId,
        },
    },
    linkTitle: t('global__viewAll'),
}));

const trayHeaderLink = computed(() => {
    if (!props.shouldShowHeaderLink) {
        return null;
    }
    return props.headerLink || defaultHeaderLink.value;
});

const recommendationQuery = computed(() => {
    const query = {
        itemsPerPage: props.maxProducts,
    };

    if (props.applySizePreferencesAndAvailability) {
        if (applyAvailabilityFilterToTrays.value && applyAvailabilityFilterPreference.value) {
            query[CATALOG_QUERY_IN_STOCK] = 'true';
        }

        if (applySizeFiltersToTrays.value && !isEmpty(preferredSizeList.value)) {
            query[CATALOG_QUERY_SIZE] = preferredSizeList.value;
        }
    }

    return merge(query, (props.categoryQuery || {}));
});

const shouldShowProductTray = computed(() => products.value.length >= 4);

const headerTitle = computed(() => props.headerTitle || t('product__recommendationTray--label'));

const style = computed(() => {
    if (props.paddingBottom && shouldShowProductTray.value) {
        return {
            paddingBottom: props.paddingBottom,
        };
    }

    return {};
});

async function getSimilarProducts() {
    try {
        // NU_EXPERIMENT: productSimilarityPdp
        // https://urbnit.atlassian.net/browse/TYP-34099
        // remove below if necessary when experiment is over
        const { products: fetchedProducts, responseId } =
            await (props.recTrayType ? getVisuallySimilarProductsByChoiceId : getSimilarProductsByChoiceId)({
                r15Svc,
                choiceId: props.choiceId,
                query: recommendationQuery.value,
                catalogFilterWhitelist: catalogFilterWhitelist.value,
                recTrayType: props.recTrayType,
                logger,
            });

        catalogResponseId.value = responseId;

        if (props.hasProductsRelatedToColorData && !props.isBuyMode) {
            const { products: productsRelatedToColor } = await getProductsByChoiceId(
                r15Svc,
                props.productsRelatedToColorData,
            );

            // ADDITIONAL CALL TO ANALYTICS VIEW_ITEM_LIST FOR SHOP THE LOOK
            trackGlobalEvent(getCatalogTrayAnalyticsData({
                products: productsRelatedToColor,
                catalogResponseId: catalogResponseId.value,
                filters: recommendationQuery.value,
                traceId: props.traceId,
                listName: CATALOG_SHOP_THE_LOOK_TRAY_LIST_NAME,
                listId: route.params.slug,
            }));

            products.value = shapeShopTheLookProducts(fetchedProducts, productsRelatedToColor);
        } else {
            products.value = fetchedProducts;
        }
    } catch (error) {
        logger.debugError(`
            Failed to fetch YMAL items for ${props.choiceId}: `, error,
        );
        Promise.reject(error);
    } finally {
        isLoaded.value = true;
    }
}

watch(() => props.choiceId, async (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
        isLoaded.value = false;
        await getSimilarProducts();
    }
});

onMounted(async () => {
    await getSimilarProducts();
});
</script>

<style lang="scss">

</style>
